import { useContext } from 'react';
import type { FC, ReactNode } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import Tooltip from 'app/components/Tooltip';
import UserContext from 'app/common/UserContext';

import style from './style.scss';

interface Props {
  incidents: MatchIncident[];
  className?: string;
  contentClassName?: string;
  alignment?: Alignment;
  children: ReactNode;
}

interface NotesProps {
  label: string;
  notes: string;
  withLabel: boolean;
  className?: string;
}

const IncidentNotes: FC<NotesProps> = ({ label, notes, withLabel, className }) => {
  const items = notes.split('\n');
  return (
    <>
      {withLabel && <div className={style.label}>{label}</div>}
      <ul className={classnames(style.notes, { [style.notes_withLabel]: withLabel }, className)}>
        {items.map((item, index) => (
          <li key={index} className={style.note}>
            {item}
          </li>
        ))}
      </ul>
    </>
  );
};

IncidentNotes.displayName = 'IncidentNotes';

const IncidentTooltip: FC<Props> = ({
  incidents,
  children,
  className,
  contentClassName,
  alignment
}) => {
  const [t] = useTranslation();
  const { userType } = useContext(UserContext);

  const labelDisplayed = userType === 'pandascore';

  const content = (
    <div className={classnames(style.tooltip, contentClassName)}>
      {incidents.map(incident => (
        <div key={incident.id} className={style.incident}>
          {incident.category && (
            <IncidentNotes
              className={style.bold}
              label=""
              withLabel={false}
              notes={t(incident.category)}
            />
          )}
          {incident.notes && (
            <IncidentNotes
              label={t('incident-public_label')}
              withLabel={labelDisplayed}
              notes={incident.notes}
            />
          )}
          {incident.private_notes && (
            <IncidentNotes
              label={t('incident-internal_label')}
              withLabel={labelDisplayed}
              notes={incident.private_notes}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip
      className={className}
      position="bottom"
      alignment={alignment}
      content={content}
      withPortal
    >
      {children}
    </Tooltip>
  );
};

IncidentTooltip.displayName = 'IncidentTooltip';

export default IncidentTooltip;
